import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { supabaseAdmin, supabase } from '../../../lib/supabase';
import { auditReportService } from '../../../services/auditReportService';
import toast from 'react-hot-toast';

/* ─── parse Supabase timestamps correctly ───────────────────────────── */
const parseSupabaseDate = (raw) => {
  if (!raw) return null;
  if (raw.includes('+') || raw.endsWith('Z')) return new Date(raw);
  return new Date(raw.replace(' ', 'T') + 'Z');
};

const formatDateTime = (raw) => {
  const d = parseSupabaseDate(raw);
  if (!d || isNaN(d)) return '—';
  return d.toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZone: 'Asia/Kolkata',
  });
};

/* ─── Helper function to extract file path from URL ─── */
const extractFilePathFromUrl = (fileUrl) => {
  if (!fileUrl) return null;
  try {
    // URL format: https://[project].supabase.co/storage/v1/object/public/[bucket]/[file-path]
    const urlParts = fileUrl.split('/public/');
    if (urlParts.length > 1) {
      return urlParts[1]; // Returns "free_decks_analysis/filename.pdf"
    }
    return null;
  } catch (error) {
    console.error('Error extracting file path:', error);
    return null;
  }
};

/* ─── Main Component ────────────────────────────────────────────────── */
const WebsiteUserManagement = ({ onUserAction, onBulkAction }) => {
  const [users,           setUsers]           = useState([]);
  const [isLoading,       setIsLoading]       = useState(true);
  const [selectedUsers,   setSelectedUsers]   = useState([]);
  const [searchQuery,     setSearchQuery]     = useState('');
  const [statusFilter,    setStatusFilter]    = useState('all');
  const [sortBy,          setSortBy]          = useState('created_at');
  const [sortOrder,       setSortOrder]       = useState('desc');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [transferringId,  setTransferringId]  = useState(null);
  const [deletingId,      setDeletingId]      = useState(null);

  useEffect(() => { fetchCustomerUsers(); }, []);

  const fetchCustomerUsers = async () => {
    try {
      setIsLoading(true);

      /* ── Fetch free deck analysis with all fields ── */
      const { data: profilesData, error: profilesError } = await supabaseAdmin
        ?.from('free_deck_analysis')
        ?.select(`
          id,
          email,
          name,
          mobile_number,
          deck_file_url,
          created_at
        `);

      if (profilesError) throw profilesError;

      /* ── Check which users have already been transferred ── */
      const { data: submissionsData, error: submissionsError } = await supabaseAdmin
        ?.from('deck_submissions')
        ?.select('user_id');

      if (submissionsError) console.error('Error fetching submissions:', submissionsError);

      const transformedUsers = (profilesData || []).map(profile => {
        const isTransferred = submissionsData?.some(sub => sub.user_id === profile.id) || false;
        
        return {
          id:               profile.id,
          email:            profile.email,
          fullName:         profile.name || 'N/A',
          mobile:           profile.mobile_number || null,
          deckFileUrl:      profile.deck_file_url || null,
          registrationDate: profile.created_at,
          status:           'active',
          isTransferred:    isTransferred,
        };
      });

      setUsers(transformedUsers);
    } catch (error) {
      console.error('Error fetching customer users:', error);
      toast.error('Failed to load customers');
    } finally {
      setIsLoading(false);
    }
  };

  /* ─── Delete File from Storage ─── */
  const deleteFileFromStorage = async (fileUrl) => {
    if (!fileUrl) return true; // No file to delete
    
    const filePath = extractFilePathFromUrl(fileUrl);
    if (!filePath) {
      console.warn('Could not extract file path from URL:', fileUrl);
      return true; // Continue with deletion even if file path extraction fails
    }
    
    try {
      const { error } = await supabaseAdmin.storage
        .from('free_decks_analysis')
        .remove([filePath]);
      
      if (error) {
        console.error('Error deleting file from storage:', error);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting file:', error);
      return false;
    }
  };

  /* ─── Delete User from Database ─── */
  const deleteUserFromDatabase = async (userId) => {
    try {
      const { error } = await supabaseAdmin
        .from('free_deck_analysis')
        .delete()
        .eq('id', userId);
      
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting user from database:', error);
      return false;
    }
  };

  /* ─── Complete Delete User (Storage + Database) ─── */
  const handleDeleteUser = async (user) => {
    try {
      setDeletingId(user.id);
      
      // Step 1: Delete file from storage (if exists)
      let fileDeleted = true;
      if (user.deckFileUrl) {
        toast.loading('Deleting file...', { id: 'delete-file' });
        fileDeleted = await deleteFileFromStorage(user.deckFileUrl);
        if (fileDeleted) {
          toast.success('File deleted', { id: 'delete-file', duration: 2000 });
        } else {
          toast.error('Failed to delete file, but continuing...', { id: 'delete-file', duration: 2000 });
        }
      }
      
      // Step 2: Delete user from database
      toast.loading('Deleting user record...', { id: 'delete-user' });
      const dbDeleted = await deleteUserFromDatabase(user.id);
      
      if (dbDeleted) {
        toast.success('User deleted successfully', { id: 'delete-user', icon: '🗑️' });
        
        // Refresh the list
        await fetchCustomerUsers();
        setDeleteConfirmId(null);
      } else {
        toast.error('Failed to delete user record', { id: 'delete-user' });
      }
      
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    } finally {
      setDeletingId(null);
      toast.dismiss('delete-file');
      toast.dismiss('delete-user');
    }
  };

  /* ─── Bulk Delete Function ─── */
  const handleBulkDelete = async (userIds) => {
    const usersToDelete = users.filter(u => userIds.includes(u.id));
    
    if (!confirm(`Delete ${usersToDelete.length} user(s) and their associated files? This action cannot be undone.`)) return;
    
    let successCount = 0;
    let failCount = 0;
    let fileDeleteSuccess = 0;
    let fileDeleteFail = 0;
    
    for (const user of usersToDelete) {
      try {
        // Delete file from storage
        if (user.deckFileUrl) {
          const fileDeleted = await deleteFileFromStorage(user.deckFileUrl);
          if (fileDeleted) fileDeleteSuccess++;
          else fileDeleteFail++;
        }
        
        // Delete user from database
        const dbDeleted = await deleteUserFromDatabase(user.id);
        if (dbDeleted) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (error) {
        console.error(`Error deleting user ${user.id}:`, error);
        failCount++;
      }
    }
    
    // Refresh the list
    await fetchCustomerUsers();
    setSelectedUsers([]);
    
    toast.success(
      `Bulk delete completed: ${successCount} users deleted, ${failCount} failed. Files: ${fileDeleteSuccess} deleted, ${fileDeleteFail} failed.`,
      { duration: 5000 }
    );
  };

  /* ─── Transfer Function ─── */
  const handleTransferUser = async (freeDeckId, userId = null) => {
    try {
      setTransferringId(freeDeckId);

      const { data, error } = await supabase.rpc(
        'transfer_free_deck_to_user_and_deck',
        {
          p_free_deck_id: freeDeckId,
          p_user_id: userId
        }
      );

      if (error) {
        console.error('Transfer error:', error);
        toast.error(`Transfer failed: ${error.message}`);
        return false;
      }

      if (!data) {
        toast.error('Unexpected server response');
        return false;
      }

      if (data.success) {
        // 🔥 Create auth user
        const { error: fnError } = await supabase.functions.invoke(
          "create-auth-user",
          {
            body: {
              user_id: data.user_id,
              email: data.email,
              full_name: data.full_name
            }
          }
        );

        if (fnError) {
          if (fnError.message?.includes("already registered")) {
            console.log("User already exists in auth");
          } else {
            console.error("Auth creation failed:", fnError);
            toast.error("User created but auth sync failed");
          }
        }

        toast.success('User created and deck transferred successfully!', {
          icon: '✅'
        });

        await fetchCustomerUsers();
        return true;

      } else {
        switch (data.code) {
          case 'USER_ALREADY_EXISTS':
            toast.error('User already exists. Transfer blocked.', { icon: '⚠️' });
            break;
          case 'USER_NOT_FOUND':
            toast.error('User not found.', { icon: '❌' });
            break;
          case 'NOT_FOUND':
            toast.error('Free deck record not found.', { icon: '❌' });
            break;
          default:
            toast.error(data.error || 'Transfer failed');
        }
        return false;
      }

    } catch (error) {
      console.error('Transfer error:', error);
      toast.error(`Transfer failed: ${error.message}`);
      return false;
    } finally {
      setTransferringId(null);
    }
  };
  /* ─── Bulk Transfer Function ─── */
  const handleBulkTransfer = async (freeDeckIds) => {
    if (!confirm(`Transfer ${freeDeckIds.length} user(s) to user_profiles and deck_submissions?`)) return;
    
    let successCount = 0;
    let failCount = 0;
    
    for (const freeDeckId of freeDeckIds) {
      const success = await handleTransferUser(freeDeckId, null);
      if (success) {
        successCount++;
      } else {
        failCount++;
      }
    }
    
    toast.success(
      `Bulk transfer completed: ${successCount} successful, ${failCount} failed`,
      { duration: 5000 }
    );
  };

  /* ─── File Preview / Download ─── */
  const handleViewFile = (fileUrl) => {
    if (!fileUrl) {
      toast.error('No file available for this user');
      return;
    }
    
    // Open in new tab
    window.open(fileUrl, '_blank');
  };

  /* ─── Options ── */
  const statusOptions = [
    { value: 'all',              label: 'All Status' },
    { value: 'active',           label: 'Active' },
    { value: 'inactive',         label: 'Inactive' },
    { value: 'pending',          label: 'Pending' },
    { value: 'transferred',      label: 'Transferred' },
    { value: 'pending_transfer', label: 'Pending Transfer' },
    { value: 'has_file',         label: 'Has File' },
    { value: 'no_file',          label: 'No File' },
  ];

  const sortOptions = [
    { value: 'created_at', label: 'Deck Submitted Date' },
    { value: 'email',      label: 'Email' },
  ];

  /* ─── Filter + sort ── */
  const filteredUsers = users
    .filter(user => {
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        user.email?.toLowerCase().includes(q) ||
        user.fullName?.toLowerCase().includes(q);
      
      let matchesStatus = true;
      if (statusFilter === 'transferred') {
        matchesStatus = user.isTransferred === true;
      } else if (statusFilter === 'pending_transfer') {
        matchesStatus = user.isTransferred === false;
      } else if (statusFilter === 'has_file') {
        matchesStatus = !!user.deckFileUrl;
      } else if (statusFilter === 'no_file') {
        matchesStatus = !user.deckFileUrl;
      } else if (statusFilter !== 'all') {
        matchesStatus = user.status === statusFilter;
      }
      
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'created_at') cmp = parseSupabaseDate(a.registrationDate) - parseSupabaseDate(b.registrationDate);
      else if (sortBy === 'email') cmp = a.email?.localeCompare(b.email);
      return sortOrder === 'asc' ? cmp : -cmp;
    });

  /* ─── Handlers ── */
  const handleSelectAll = (checked) => setSelectedUsers(checked ? filteredUsers.map(u => u.id) : []);
  const handleSelectUser = (id, checked) =>
    setSelectedUsers(checked ? [...selectedUsers, id] : selectedUsers.filter(x => x !== id));

  const handleSort = (field) => {
    if (sortBy === field) setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortOrder('desc'); }
  };

  /* ─── Badges ── */
  const getStatusBadge = (status, isTransferred) => {
    if (isTransferred) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">Transferred</span>;
    }
    
    const cfg = {
      active:   { color: 'bg-success/10 text-success', label: 'Active' },
      inactive: { color: 'bg-muted text-muted-foreground', label: 'Inactive' },
      pending:  { color: 'bg-warning/10 text-warning', label: 'Pending' },
    };
    const c = cfg[status] || cfg.pending;
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${c.color}`}>{c.label}</span>;
  };

  /* ─── Shared customer info block ── */
  const CustomerInfoBlock = ({ user }) => (
    <div className="flex flex-col">
      <span className="text-sm font-medium text-foreground">{user.fullName}</span>
      <span className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
        <Icon name="Mail" size={12} />{user.email}
      </span>
      {user.mobile && (
        <span className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
          <Icon name="Phone" size={12} />{user.mobile}
        </span>
      )}
    </div>
  );

  /* ─── File Preview Component ─── */
  const FilePreview = ({ user }) => {
    if (!user.deckFileUrl) {
      return (
        <span className="text-xs text-muted-foreground italic">No file</span>
      );
    }
    
    const fileName = user.deckFileUrl.split('/').pop();
    const fileExtension = fileName?.split('.').pop()?.toLowerCase();
    
    const getFileIcon = () => {
      if (fileExtension === 'pdf') return 'FileText';
      if (fileExtension === 'ppt' || fileExtension === 'pptx') return 'Presentation';
      return 'File';
    };
    
    return (
      <div className="flex items-center gap-2">
        <Icon name={getFileIcon()} size={14} className="text-primary" />
        <span className="text-xs text-muted-foreground truncate max-w-[150px]" title={fileName}>
          {fileName}
        </span>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            iconName="Eye"
            onClick={() => handleViewFile(user.deckFileUrl)}
            className="h-6 w-6 p-0"
            title="View file"
          />
        </div>
      </div>
    );
  };

  /* ─── Action buttons ── */
  const ActionButtons = ({ user, mobile = false }) => {
    const variant = mobile ? 'outline' : 'ghost';
    const isDeleting = deletingId === user.id;
    
    return (
      <div className={`flex items-center gap-2 ${mobile ? 'flex-wrap' : ''}`}>
        {/* Transfer Button */}
        {!user.isTransferred && (
          <Button 
            variant="outline" 
            size="sm" 
            iconName="ArrowRight" 
            onClick={() => handleTransferUser(user.id, null)}
            disabled={transferringId === user.id || isDeleting}
            className="bg-primary/5 hover:bg-primary/10"
          >
            {transferringId === user.id ? 'Transferring...' : 'Transfer'}
          </Button>
        )}
        <Button variant={variant} size="sm" iconName="Mail" onClick={() => onUserAction?.('email', user.id)}>Email</Button>
        {deleteConfirmId === user.id ? (
          <>
            <Button 
              variant="destructive" 
              size="sm" 
              iconName="Trash2" 
              onClick={() => handleDeleteUser(user)}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Confirm'}
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              iconName="X" 
              onClick={() => setDeleteConfirmId(null)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
          </>
        ) : (
          <Button 
            variant="ghost" 
            size="sm" 
            iconName="Trash2" 
            onClick={() => setDeleteConfirmId(user.id)}
            disabled={isDeleting}
          />
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="bg-card rounded-lg shadow-md border border-border p-8">
        <div className="flex items-center justify-center">
          <Icon name="Loader2" className="w-6 h-6 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">Loading customer users...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-md border border-border">

      {/* ── Header ── */}
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-foreground flex items-center gap-2">
              <Icon name="Users" size={20} className="text-primary" />
              Website User Management
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {users.length} free deck submitted user{users.length !== 1 ? 's' : ''} ·{' '}
              {users.filter(u => !u.isTransferred).length} pending transfer ·{' '}
              {users.filter(u => u.isTransferred).length} transferred ·{' '}
              {users.filter(u => u.deckFileUrl).length} with files
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              type="search"
              placeholder="Search customers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full sm:w-64"
            />
            <Select options={statusOptions} value={statusFilter} onChange={setStatusFilter} className="w-full sm:w-48" />
            <Select options={sortOptions} value={sortBy} onChange={setSortBy} className="w-full sm:w-48" />
          </div>
        </div>

        {selectedUsers.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2 p-3 bg-muted rounded-lg">
            <span className="text-sm text-foreground font-medium">{selectedUsers.length} selected</span>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" iconName="ArrowRight" onClick={() => handleBulkTransfer(selectedUsers)}>
                Transfer Selected
              </Button>
              <Button variant="destructive" size="sm" iconName="Trash2" onClick={() => handleBulkDelete(selectedUsers)}>
                Delete Selected
              </Button>
              <Button variant="outline" size="sm" iconName="CheckCircle2" onClick={() => onBulkAction?.('activate', selectedUsers)}>Activate</Button>
              <Button variant="outline" size="sm" iconName="XCircle" onClick={() => onBulkAction?.('deactivate', selectedUsers)}>Deactivate</Button>
              <Button variant="outline" size="sm" iconName="Mail" onClick={() => onBulkAction?.('email', selectedUsers)}>Send Email</Button>
              <Button variant="ghost" size="sm" iconName="X" onClick={() => setSelectedUsers([])}>Clear</Button>
            </div>
          </div>
        )}
      </div>

      {/* ── Desktop Table ── */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-6 py-3 text-left">
                <Checkbox
                  checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                />
              </th>
              {[
                { label: 'Customer Info', field: 'email' },
                { label: 'Submitted', field: 'created_at' },
                { label: 'File', field: null },
                { label: 'Status', field: null },
                { label: 'Actions', field: null },
              ].map(({ label, field }) => (
                <th
                  key={label}
                  className={`px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider ${field ? 'cursor-pointer hover:text-foreground' : ''}`}
                  onClick={() => field && handleSort(field)}
                >
                  <div className="flex items-center gap-2">
                    {label}
                    {field && sortBy === field && (
                      <Icon name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={14} />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {filteredUsers.map(user => (
              <tr key={user.id} className="hover:bg-muted/30 transition-smooth">
                <td className="px-6 py-4">
                  <Checkbox
                    checked={selectedUsers.includes(user.id)}
                    onChange={(e) => handleSelectUser(user.id, e?.target?.checked)}
                  />
                </td>
                <td className="px-6 py-4"><CustomerInfoBlock user={user} /></td>
                <td className="px-6 py-4 text-sm text-foreground whitespace-nowrap">{formatDateTime(user.registrationDate)}</td>
                <td className="px-6 py-4"><FilePreview user={user} /></td>
                <td className="px-6 py-4">{getStatusBadge(user.status, user.isTransferred)}</td>
                <td className="px-6 py-4"><ActionButtons user={user} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Mobile Cards ── */}
      <div className="lg:hidden divide-y divide-border">
        {filteredUsers.map(user => (
          <div key={user.id} className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-start gap-3 flex-1">
                <Checkbox
                  checked={selectedUsers.includes(user.id)}
                  onChange={(e) => handleSelectUser(user.id, e?.target?.checked)}
                />
                <div className="flex-1 min-w-0">
                  <CustomerInfoBlock user={user} />
                </div>
              </div>
              {getStatusBadge(user.status, user.isTransferred)}
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
              <div>
                <span className="text-muted-foreground text-xs">Registered</span>
                <p className="text-foreground font-medium text-xs mt-0.5">{formatDateTime(user.registrationDate)}</p>
              </div>
              <div>
                <span className="text-muted-foreground text-xs">File</span>
                <div className="mt-0.5"><FilePreview user={user} /></div>
              </div>
            </div>

            <ActionButtons user={user} mobile />
          </div>
        ))}
      </div>

      {filteredUsers.length === 0 && (
        <div className="p-8 md:p-12 text-center">
          <Icon name="Users" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
          <p className="text-muted-foreground">No customers found matching your criteria</p>
        </div>
      )}

    </div>
  );
};

export default WebsiteUserManagement;